import React, { useEffect, useState } from "react";
import axios from "axios";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { CircularProgress, Container, Typography } from "@mui/material";
import config from "../config.js";

// Register necessary Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

function PieChartDisplay() {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { API_BASE_URL } = config;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const response = await axios.get(`${API_BASE_URL}/api/v1/workbookToJson`);
      
        
        // Check if response.data and response.data.data exist before trying to access Sheet1
        if (!response.data || !response.data.data || !response.data.data.Sheet1) {
          throw new Error("Invalid data format received from API");
        }
        
        const rawData = response.data.data.Sheet1;
        
        // Filter data - make sure we have items with the right properties
        const filteredData = rawData.filter((item, index) => 
          index !== 0 && 
          item["Personal Budget"] !== "Total" && 
          typeof item["__EMPTY"] === "number"
        );
        
        // Extract labels and data
        const labels = filteredData.map(item => item["__EMPTY_1"] || "Unnamed");
        const data = filteredData.map(item => item["__EMPTY"] || 0);
        
        // Generate colors dynamically based on number of data points
        const backgroundColors = generateColors(filteredData.length, 0.7);
        const borderColors = generateColors(filteredData.length, 1);
        
        setChartData({ labels, data, backgroundColors, borderColors });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error);
        setLoading(false);
      }
    };

    fetchData();
  }, [API_BASE_URL]);

  // Function to generate colors
  const generateColors = (count, alpha) => {
    const colors = [];
    for (let i = 0; i < count; i++) {
      const hue = (i * 137.508) % 360; 
      colors.push(`hsla(${hue}, 70%, 60%, ${alpha})`);
    }
    return colors;
  };

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Error Loading Chart
        </Typography>
        <Typography>
          {error.message || "Failed to load chart data"}
        </Typography>
      </Container>
    );
  }

  if (loading || !chartData) {
    return (
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading chart data...</Typography>
      </Container>
    );
  }

  // Setup the chart data
  const data = {
    labels: chartData.labels,
    datasets: [
      {
        label: "Budget Expenses",
        data: chartData.data,
        backgroundColor: chartData.backgroundColors,
        borderColor: chartData.borderColors,
        borderWidth: 1,
      },
    ],
  };

  // Chart options
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: $${value} (${percentage}%)`;
          }
        }
      }
    },
  };

  return (
    <Container>
      <Typography variant="h4" align="center" gutterBottom sx={{ my: 2 }}>
        Personal Budget Expenses
      </Typography>
      
      <div
        style={{
          padding: "20px",
          width: "100%",
          maxHeight: "600px",
        }}
      >
        <Pie data={data} options={options} />
      </div>
    </Container>
  );
}

export default PieChartDisplay;

